import styled from "styled-components";
import { fontFamilies } from "components/shared/tokens/typography";

const COLORS = {
  page: "#f5f7fb",
  surface: "#ffffff",
  text: "#293142",
  muted: "#7b8498",
  subtle: "#9197a6",
  border: "#e8ebf1",
  brand: "#ef4f5f",
};

export const Page = styled.main`
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  background: ${COLORS.page};
  color: ${COLORS.text};
  font-family: ${fontFamilies.regular};
`;

export const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${COLORS.surface};
  border-bottom: 1px solid ${COLORS.border};
`;

export const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  min-height: 64px;
  padding: 10px 16px;
`;

export const HeaderTitle = styled.h1`
  margin: 0;
  font-family: ${fontFamilies.semiBold};
  font-size: 16px;
  line-height: 1.4;
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

export const IconButton = styled.button`
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLORS.border};
  border-radius: 50%;
  background: ${COLORS.surface};
  color: ${COLORS.text};
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`;

export const SearchRow = styled.div`
  padding: 0 16px 12px;
`;

export const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid ${COLORS.border};
  border-radius: 12px;
  padding: 10px 12px;
  background: ${COLORS.surface};
`;

export const SearchInput = styled.input`
  min-width: 0;
  flex: 1;
  border: 0;
  outline: 0;
  color: ${COLORS.text};
  font: inherit;
  font-size: 14px;

  &::placeholder {
    color: ${COLORS.subtle};
  }
`;

export const PageTabs = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
`;

export const PageTab = styled.button`
  position: relative;
  min-height: 52px;
  border: 0;
  border-bottom: 3px solid
    ${(props) => (props.$active ? COLORS.brand : "transparent")};
  background: ${(props) =>
    props.$active
      ? "linear-gradient(180deg, #ffffff 0%, #fff5f6 100%)"
      : COLORS.surface};
  color: ${(props) => (props.$active ? COLORS.text : COLORS.muted)};
  font-family: ${(props) =>
    props.$active ? fontFamilies.semiBold : fontFamilies.regular};
  font-size: 14px;
  cursor: pointer;
`;

export const CountBadge = styled.span`
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  margin-left: 5px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 0 5px;
  background: ${COLORS.brand};
  color: ${COLORS.surface};
  font-size: 10px;
`;

export const Content = styled.div`
  display: flex;
  width: 100%;
  min-height: calc(100vh - 116px);
  min-height: calc(100dvh - 116px);
  flex-direction: column;
  padding: 16px 16px 32px;
`;

export const StatusFilters = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  margin: 0 -16px 14px;
  padding: 0 16px 4px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const StatusFilter = styled.button`
  flex: 0 0 auto;
  border: 1px solid
    ${(props) => (props.$active ? COLORS.brand : COLORS.border)};
  border-radius: 9px;
  padding: 9px 12px;
  background: ${(props) => (props.$active ? "#fff5f6" : COLORS.surface)};
  color: ${(props) => (props.$active ? COLORS.brand : COLORS.muted)};
  font-family: ${(props) =>
    props.$active ? fontFamilies.semiBold : fontFamilies.regular};
  font-size: 13px;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(34, 45, 65, 0.04);
`;

export const CardList = styled.div`
  display: grid;
  gap: 14px;
`;

export const Card = styled.article`
  overflow: hidden;
  border: 1px solid ${COLORS.border};
  border-radius: 18px;
  background: ${COLORS.surface};
  box-shadow: 0 4px 14px rgba(34, 45, 65, 0.05);
`;

export const CardTop = styled.div`
  padding: 0 16px 8px;
`;

export const StatusImage = styled.img`
  display: block;
  width: auto;
  max-width: 100%;
  height: 22px;
  object-fit: contain;
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  border-radius: 5px;
  padding: 4px 9px;
  background: #f0f1f4;
  color: ${COLORS.muted};
  font-family: ${fontFamilies.semiBold};
  font-size: 12px;
  line-height: 1.2;
`;

export const CardHeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 7px;
`;

export const CardHeading = styled.div`
  min-width: 0;
`;

export const PONumber = styled.h2`
  overflow: hidden;
  margin: 0;
  color: ${COLORS.text};
  font-family: ${fontFamilies.bold};
  font-size: 16px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const POSummary = styled.p`
  margin: 2px 0 0;
  color: ${COLORS.muted};
  font-size: 12px;
`;

export const CardActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
`;

export const CardAction = styled(IconButton)`
  width: 38px;
  height: 38px;
  padding: ${({ $imageOnly }) => ($imageOnly ? "0" : "initial")};
  border: ${({ $imageOnly }) => ($imageOnly ? "none" : `1px solid ${COLORS.border}`)};
  background: ${({ $imageOnly }) => ($imageOnly ? "transparent" : COLORS.surface)};
  color: ${COLORS.brand};
`;

export const MetaPanel = styled.div`
  display: grid;
  min-height: 92px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  margin: 0 16px 12px;
  border: 1px solid ${COLORS.border};
  border-radius: 13px;
  padding: 8px 6px;
`;

export const MetaItem = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: ${COLORS.muted};
`;

export const MetaLabel = styled.span`
  margin-top: 1px;
  color: ${COLORS.subtle};
  font-size: 10px;
  line-height: 1.2;
`;

export const MetaValue = styled.span`
  max-width: 100%;
  overflow: hidden;
  color: ${COLORS.text};
  font-family: ${fontFamilies.semiBold};
  font-size: 12px;
  line-height: 1.25;
  text-align: center;
  text-overflow: ellipsis;
  white-space: ${(props) => (props.$wrap ? "normal" : "nowrap")};

  ${(props) =>
    props.$wrap &&
    `
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    `}
`;

export const StateContainer = styled.div`
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
`;

export const StateImage = styled.img`
  width: 112px;
  height: 112px;
`;

export const StateTitle = styled.h2`
  margin: 18px 0 8px;
  font-family: ${fontFamilies.bold};
  font-size: 17px;
`;

export const StateMessage = styled.p`
  max-width: 310px;
  margin: 0;
  color: ${COLORS.muted};
  font-size: 14px;
  line-height: 1.5;
`;

export const StateButton = styled.button`
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  border: 0;
  border-radius: 10px;
  padding: 11px 18px;
  background: ${COLORS.brand};
  color: ${COLORS.surface};
  font-family: ${fontFamilies.semiBold};
  font-size: 13px;
  cursor: pointer;
`;

export const StateButtonCount = styled.span`
  display: inline-flex;
  min-width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 0 5px;
  background: ${COLORS.surface};
  color: ${COLORS.brand};
  font-size: 10px;
  line-height: 1;
`;

export const ScrollLoader = styled.div`
  display: flex;
  justify-content: center;
  padding: 18px 0;
`;
